/**
 * AddContactInline — Inline expanding card replacement for AddContactModal.
 *
 * Wires to existing POST /api/v1/admin/contacts endpoint (same backend
 * contract) but rendered as a slot-in card above the lead table — no modal,
 * no scrim, no focus trap.
 *
 * Source is prefilled from the active tab (e.g. 'holiday', 'referral',
 * 'contact'). Admin can override.
 */
import { useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import { Btn } from '../ui/primitives';

const SOURCE_OPTIONS = [
  { value: 'contact', label: 'Contact form' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'letstalktaxes', label: "Let's Talk Taxes" },
  { value: 'referral', label: 'Referral' },
  { value: 'coffee', label: 'Coffee chat' },
  { value: 'ppc_ads', label: 'PPC ad' },
  { value: 'manual', label: 'Manual entry' },
];

export default function AddContactInline({
  defaultSource = 'manual',
  onClose,
  onCreate,
  isLoading,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    business_name: '',
    source: defaultSource,
    status: 'new',
    notes: '',
  });

  const set = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target.value }));

  const valid = form.first_name.trim() && form.email.includes('@');

  const handleSubmit = () => {
    if (!valid) return;
    onCreate(form);
  };

  return (
    <div data-testid="add-contact-inline" className="lb-inline-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>Add a contact</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>Source pre-filled to <b>{form.source}</b>; adjust below if needed.</p>
        </div>
        <Btn data-testid="add-contact-inline-btn-3" variant="ghost" sm icon={X} iconOnly onClick={onClose} testid="add-contact-close" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        <label className="lb-field-row">
          <span className="lb-lbl">First name *</span>
          <input data-testid="add-contact-first-name" className="lb-inp" value={form.first_name} onChange={set('first_name')} placeholder="Sara" autoFocus />
        </label>
        <label className="lb-field-row">
          <span className="lb-lbl">Last name</span>
          <input data-testid="add-contact-last-name" className="lb-inp" value={form.last_name} onChange={set('last_name')} placeholder="Smith" />
        </label>
        <label className="lb-field-row">
          <span className="lb-lbl">Email *</span>
          <input data-testid="add-contact-email" className="lb-inp" type="email" value={form.email} onChange={set('email')} placeholder="sara@example.com" />
        </label>
        <label className="lb-field-row">
          <span className="lb-lbl">Phone</span>
          <input data-testid="add-contact-phone" className="lb-inp" value={form.phone} onChange={set('phone')} placeholder="(704) 555-0100" />
        </label>
        <label className="lb-field-row">
          <span className="lb-lbl">Business name</span>
          <input data-testid="add-contact-business" className="lb-inp" value={form.business_name} onChange={set('business_name')} placeholder="Acme LLC" />
        </label>
        <label className="lb-field-row">
          <span className="lb-lbl">Source</span>
          <select data-testid="add-contact-source" className="lb-field-select" value={form.source} onChange={set('source')} style={{ width: '100%' }}>
            {SOURCE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>

      <label className="lb-field-row" style={{ marginTop: 10 }}>
        <span className="lb-lbl">Notes</span>
        <textarea data-testid="add-contact-notes" className="lb-inp" rows={2} value={form.notes} onChange={set('notes')} placeholder="Optional context (how you met, next steps…)" />
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        <Btn data-testid="add-contact-inline-btn-2" variant="ghost" sm onClick={onClose} testid="add-contact-cancel">Cancel</Btn>
        <Btn data-testid="add-contact-inline-btn-1" variant="primary" sm icon={UserPlus} onClick={handleSubmit} disabled={!valid || isLoading} testid="add-contact-submit">
          {isLoading ? 'Adding…' : 'Add contact'}
        </Btn>
      </div>
    </div>
  );
}
